import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { colors } from 'settings/colors'

// Soil-moisture (AWC) deficit banding for the Water Monitoring "Current" chip. Copied into the
// dashboards module from App/Map/_utils/getSoilAWCBackgroundColor + moderateDeficitThreshold
// (copy-don't-modify), so the dashboard stays independent of the map surfaces. The colour tokens
// themselves are the shared `colors` (read-only), so the chip matches the map's AWC bands exactly.
const MODERATE_DEFICIT_THRESHOLD = 0.7

export type TSoilDeficitZone = 'extremeDeficit' | 'highDeficit' | 'moderateDeficit' | 'ideal' | 'saturated'

// Maps a soil-moisture value (% of available water) to its deficit zone, given the property/probe's
// MAD (management-allowable-depletion) threshold. Mirrors getSoilAWCBackgroundColor's branching.
export const getSoilDeficitZone = (value: number | null, madValue = 70): TSoilDeficitZone | null => {
  if (isNil(value)) return null

  const moderateDeficit = madValue * (1 - MODERATE_DEFICIT_THRESHOLD)

  if (value < 0) return 'extremeDeficit'

  if (value < moderateDeficit) return 'highDeficit'

  if (value < madValue) return 'moderateDeficit'

  if (value < 100) return 'ideal'

  return 'saturated'
}

export const soilDeficitZoneColor: Record<TSoilDeficitZone, string> = {
  extremeDeficit: colors.extremeDeficitZone,
  highDeficit: colors.highDeficitZone,
  moderateDeficit: colors.moderateDeficitZone,
  ideal: colors.idealZone,
  saturated: colors.saturatedZone,
}

export const soilDeficitZoneLabel = (zone: TSoilDeficitZone): string =>
  ({
    extremeDeficit: translate.phrases.banyanApp('Extreme Deficit'),
    highDeficit: translate.phrases.banyanApp('High Deficit'),
    moderateDeficit: translate.phrases.banyanApp('Moderate Deficit'),
    ideal: translate.phrases.banyanApp('Ideal'),
    saturated: translate.phrases.banyanApp('Saturated'),
  })[zone]
